import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/components/Logo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Users, Send, CheckCircle2, XCircle, Clock3, FileDown, Loader2, CalendarDays, CalendarRange, PhoneOff,
} from "lucide-react";

const MOE_LOGO = `${window.location.origin}/moe-logo.png`;

const fmtISO = (d) => d.toISOString().slice(0, 10);
const periodParams = (mode, from, to) => {
  if (mode === "month") {
    const d = new Date();
    return { start: fmtISO(new Date(d.getFullYear(), d.getMonth(), 1)) };
  }
  if (mode === "quarter") {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return { start: fmtISO(d) };
  }
  if (mode === "custom") {
    const p = {};
    if (from) p.start = from;
    if (to) p.end = to;
    return p;
  }
  return {};
};
const PERIOD_LABELS = { all: "كل الفترة", month: "هذا الشهر", quarter: "آخر 3 أشهر", custom: "فترة مخصصة" };

const MonthlyChart = ({ monthly }) => {
  const max = Math.max(1, ...monthly.map((m) => m.total));
  const hasData = monthly.some((m) => m.total > 0);
  return (
    <div data-testid="monthly-chart">
      <div className="flex items-end justify-between gap-2 sm:gap-4" style={{ height: 180 }}>
        {monthly.map((m) => {
          const totalH = Math.round((m.total / max) * 150);
          const sentH = m.total ? Math.round((m.sent / m.total) * totalH) : 0;
          return (
            <div key={m.key} className="flex flex-1 flex-col items-center justify-end gap-1" data-testid={`month-bar-${m.key}`}>
              <span className="text-xs font-bold text-slate-700">{m.total || ""}</span>
              <div className="relative flex w-full max-w-[42px] items-end justify-center rounded-t-md bg-slate-100"
                style={{ height: Math.max(totalH, 4) }} title={`${m.label}: ${m.sent} مرسلة من ${m.total}`}>
                <div className="absolute bottom-0 w-full rounded-t-md bg-gradient-to-t from-emerald-700 to-teal-500"
                  style={{ height: Math.max(sentH, m.sent ? 4 : 0) }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between gap-2 sm:gap-4">
        {monthly.map((m) => (
          <span key={m.key} className="flex-1 text-center text-[11px] text-slate-400">{m.label}</span>
        ))}
      </div>
      {!hasData && <p className="mt-3 text-center text-sm text-slate-400">لا توجد رسائل خلال الأشهر الستة الماضية</p>}
      <div className="mt-3 flex items-center justify-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-gradient-to-t from-emerald-700 to-teal-500" /> المرسلة</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-slate-100" /> الإجمالي</span>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardContent className="flex items-center gap-4 p-5">
      <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <p className="text-2xl font-extrabold text-slate-800" data-testid={`stat-${label}`}>{value ?? 0}</p>
        <p className="text-xs text-slate-400">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const TYPE_COLORS = ["#065f46", "#0d9488", "#f59e0b", "#0ea5e9", "#e11d48", "#6366f1", "#8b5cf6", "#f97316", "#14b8a6", "#64748b"];

const MonthlyTypeChart = ({ monthly }) => {
  const labels = [];
  monthly.forEach((m) => Object.keys(m.types || {}).forEach((l) => { if (!labels.includes(l)) labels.push(l); }));
  const colorOf = (l) => TYPE_COLORS[labels.indexOf(l) % TYPE_COLORS.length];
  const max = Math.max(1, ...monthly.map((m) => m.total));
  const hasData = labels.length > 0;
  return (
    <div data-testid="monthly-type-chart">
      <div className="flex items-end justify-between gap-2 sm:gap-4" style={{ height: 180 }}>
        {monthly.map((m) => {
          const barH = Math.round((m.total / max) * 150);
          return (
            <div key={m.key} className="flex flex-1 flex-col items-center justify-end gap-1">
              <span className="text-xs font-bold text-slate-700">{m.total || ""}</span>
              <div className="flex w-full max-w-[42px] flex-col-reverse overflow-hidden rounded-t-md bg-slate-50"
                style={{ height: Math.max(barH, 4) }}>
                {labels.map((l) => {
                  const cnt = (m.types || {})[l] || 0;
                  if (!cnt) return null;
                  const h = Math.round((cnt / m.total) * barH);
                  return <div key={l} style={{ height: Math.max(h, 2), background: colorOf(l) }} title={`${l}: ${cnt}`} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between gap-2 sm:gap-4">
        {monthly.map((m) => (
          <span key={m.key} className="flex-1 text-center text-[11px] text-slate-400">{m.label}</span>
        ))}
      </div>
      {!hasData && <p className="mt-3 text-center text-sm text-slate-400">لا توجد رسائل لعرض التصنيف</p>}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
        {labels.map((l) => (
          <span key={l} className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: colorOf(l) }} /> {l}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/my/report", { params: periodParams(period, customFrom, customTo) })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  }, [period, customFrom, customTo]);

  const generatePDF = () => {
    if (!data) return;
    const today = new Date().toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { year: "numeric", month: "long", day: "numeric" });
    const p = data.period || {};
    const periodLine = (p.start || p.end)
      ? `<div class="sub">الفترة: من ${p.start || "البداية"} إلى ${p.end || fmtISO(new Date())}</div>`
      : `<div class="sub">الفترة: كل الفترة</div>`;
    const maxM = Math.max(1, ...(data.monthly || []).map((m) => m.total));
    const monthBars = (data.monthly || []).map((m) => {
      const h = Math.round((m.total / maxM) * 90);
      return `<div class="mb"><span class="mv">${m.total || ""}</span><div class="bar" style="height:${Math.max(h, 2)}px"></div><span class="ml">${m.label}</span></div>`;
    }).join("");
    const typeLabels = [];
    (data.monthly || []).forEach((m) => Object.keys(m.types || {}).forEach((l) => { if (!typeLabels.includes(l)) typeLabels.push(l); }));
    const colorOf = (l) => TYPE_COLORS[typeLabels.indexOf(l) % TYPE_COLORS.length];
    const typeBars = (data.monthly || []).map((m) => {
      const barH = Math.round((m.total / maxM) * 90);
      const segs = typeLabels.map((l) => {
        const cnt = (m.types || {})[l] || 0;
        if (!cnt) return "";
        const h = Math.round((cnt / m.total) * barH);
        return `<div class="seg" style="height:${Math.max(h, 2)}px;background:${colorOf(l)}"></div>`;
      }).join("");
      return `<div class="mb"><span class="mv">${m.total || ""}</span><div class="col" style="height:${Math.max(barH, 2)}px">${segs}</div><span class="ml">${m.label}</span></div>`;
    }).join("");
    const typeLegend = typeLabels.map((l) => `<span><i style="background:${colorOf(l)}"></i>${l}</span>`).join("");
    const rows = (data.by_type || []).map((t, i) =>
      `<tr><td>${i + 1}</td><td>${t.label}</td><td>${t.total}</td><td>${t.sent}</td><td>${t.failed}</td></tr>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>تقرير متابعة المرشد</title>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet">
      <style>
        *{font-family:'Cairo',sans-serif;box-sizing:border-box;}
        body{padding:28px 34px;color:#0f172a;}
        .header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding-bottom:14px;border-bottom:3px solid #065f46;}
        .header .moe{flex:1;display:flex;justify-content:center;}
        .header .moe img{height:90px;object-fit:contain;}
        .header .maakum{flex:1;display:flex;justify-content:flex-start;}
        .header .maakum img{height:70px;object-fit:contain;}
        .header .gov{flex:1;text-align:right;line-height:1.9;}
        .header .gov .l1{font-weight:800;font-size:15px;color:#065f46;}
        .header .gov .l2{font-weight:700;font-size:14px;color:#334155;}
        .header .gov .l3{font-size:13px;color:#64748b;}
        h1.title{text-align:center;color:#065f46;font-size:22px;margin:22px 0 4px;}
        .sub{text-align:center;color:#64748b;font-size:12px;margin-bottom:20px;}
        .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:8px;}
        .box{border:1px solid #e2e8f0;border-radius:10px;padding:12px;text-align:center;}
        .box .n{font-size:24px;font-weight:800;color:#065f46;}
        .box .t{font-size:12px;color:#64748b;margin-top:2px;}
        h2{background:#ecfdf5;color:#065f46;padding:8px 12px;border-radius:8px;font-size:15px;margin:22px 0 8px;}
        table{width:100%;border-collapse:collapse;}
        th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:center;font-size:13px;}
        th{background:#f1f5f9;color:#334155;}
        td:nth-child(2){text-align:right;font-weight:600;}
        .footer{margin-top:40px;display:flex;justify-content:space-between;align-items:flex-end;}
        .sign{text-align:right;}
        .sign .lbl{font-size:12px;color:#64748b;}
        .sign .nm{font-size:15px;font-weight:800;color:#0f172a;margin-top:2px;}
        .sign .line{margin-top:28px;width:200px;border-top:1px dashed #94a3b8;}
        .date{font-size:12px;color:#94a3b8;}
        .months{display:flex;align-items:flex-end;justify-content:space-between;gap:10px;height:130px;margin-top:6px;}
        .mb{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;gap:3px;}
        .mb .bar{width:60%;max-width:34px;background:#065f46;border-radius:4px 4px 0 0;}
        .mb .mv{font-size:11px;font-weight:700;color:#334155;}
        .mb .ml{font-size:10px;color:#64748b;}
        .mb .col{width:60%;max-width:34px;display:flex;flex-direction:column-reverse;overflow:hidden;border-radius:4px 4px 0 0;}
        .mb .seg{width:100%;}
        .legend{display:flex;flex-wrap:wrap;justify-content:center;gap:10px;margin-top:8px;font-size:11px;color:#475569;}
        .legend span{display:flex;align-items:center;gap:4px;}
        .legend i{width:12px;height:12px;border-radius:3px;display:inline-block;}
        @media print{.noprint{display:none;}}
      </style></head><body>
      <div class="header">
        <div class="maakum"><img src="${LOGO_URL}" alt="معاكم" crossorigin="anonymous"/></div>
        <div class="moe"><img src="${MOE_LOGO}" alt="وزارة التعليم" crossorigin="anonymous"/></div>
        <div class="gov">
          <div class="l1">المملكة العربية السعودية</div>
          <div class="l2">وزارة التعليم</div>
          <div class="l3">${data.school_name || ""}</div>
        </div>
      </div>

      <h1 class="title">تقرير متابعة المرشد الطلابي</h1>
      <div class="sub">تاريخ إصدار التقرير: ${today}</div>
      ${periodLine}

      <div class="grid">
        <div class="box"><div class="n">${data.messages.total}</div><div class="t">إجمالي الرسائل</div></div>
        <div class="box"><div class="n">${data.messages.sent}</div><div class="t">تم الإرسال</div></div>
        <div class="box"><div class="n">${data.messages.failed}</div><div class="t">فشل الإرسال</div></div>
        <div class="box"><div class="n">${data.students.total}</div><div class="t">عدد الطلاب</div></div>
      </div>
      <div class="grid">
        <div class="box"><div class="n">${data.today.total}</div><div class="t">رسائل اليوم</div></div>
        <div class="box"><div class="n">${data.week.total}</div><div class="t">رسائل آخر 7 أيام</div></div>
        <div class="box"><div class="n">${data.students.with_phone}</div><div class="t">طلاب لديهم جوال</div></div>
        <div class="box"><div class="n">${data.students.without_phone}</div><div class="t">طلاب بدون جوال</div></div>
      </div>

      <h2>تفصيل الرسائل حسب النوع</h2>
      <table>
        <thead><tr><th>#</th><th>نوع الرسالة</th><th>الإجمالي</th><th>تم الإرسال</th><th>فشل</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="5">لا توجد رسائل مرسلة بعد</td></tr>`}</tbody>
      </table>

      <h2>أداء الرسائل الشهري (آخر 6 أشهر)</h2>
      <div class="months">${monthBars}</div>

      <h2>توزيع الرسائل حسب النوع شهرياً</h2>
      <div class="months">${typeBars}</div>
      <div class="legend">${typeLegend}</div>

      <div class="footer">
        <div class="date">صادر عن منصة معاكم — المدرسة دائماً معكم</div>
        <div class="sign">
          <div class="lbl">المرشد الطلابي</div>
          <div class="nm">${data.counselor_name || ""}</div>
          <div class="line"></div>
        </div>
      </div>

      <button class="noprint" onclick="window.print()" style="margin-top:24px;padding:10px 20px;background:#065f46;color:#fff;border:none;border-radius:8px;cursor:pointer;font-family:'Cairo';">طباعة / حفظ PDF</button>
      <script>window.addEventListener('load',function(){setTimeout(function(){window.print();},900);});</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("يرجى السماح بالنوافذ المنبثقة لتصدير التقرير"); return; }
    w.document.write(html);
    w.document.close();
  };

  if (loading && !data) {
    return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-7 w-7 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">التقارير والإحصائيات</h1>
          <p className="text-slate-400">متابعة مفصلة لرسائلك ونشاطك، مع تقرير PDF رسمي باسمك واسم المدرسة</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="mb-1 block text-xs text-slate-500">الفترة</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الفترة</SelectItem>
                <SelectItem value="month">هذا الشهر</SelectItem>
                <SelectItem value="quarter">آخر 3 أشهر (فصل)</SelectItem>
                <SelectItem value="custom">فترة مخصصة</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === "custom" && (
            <>
              <div>
                <Label className="mb-1 block text-xs text-slate-500">من</Label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-36" data-testid="period-from" />
              </div>
              <div>
                <Label className="mb-1 block text-xs text-slate-500">إلى</Label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                  className="w-36" data-testid="period-to" />
              </div>
            </>
          )}
          <Button onClick={generatePDF} className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="download-report-button">
            <FileDown className="h-4 w-4" /> تحميل تقرير PDF
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400" data-testid="period-active-label">
        الإحصائيات المعروضة عن: <span className="font-bold text-emerald-700">{PERIOD_LABELS[period]}</span>
        {loading && <Loader2 className="mr-2 inline h-3 w-3 animate-spin" />}
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Send} label="إجمالي الرسائل" value={data.messages.total} color="bg-sky-100 text-sky-700" />
        <Stat icon={CheckCircle2} label="تم الإرسال" value={data.messages.sent} color="bg-green-100 text-green-700" />
        <Stat icon={XCircle} label="فشل الإرسال" value={data.messages.failed} color="bg-red-100 text-red-600" />
        <Stat icon={Clock3} label="قيد الإرسال" value={data.messages.pending} color="bg-amber-100 text-amber-700" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={CalendarDays} label="رسائل اليوم" value={data.today.total} color="bg-teal-100 text-teal-700" />
        <Stat icon={CalendarRange} label="رسائل آخر 7 أيام" value={data.week.total} color="bg-indigo-100 text-indigo-700" />
        <Stat icon={Users} label="طلاب لديهم جوال" value={data.students.with_phone} color="bg-emerald-100 text-emerald-700" />
        <Stat icon={PhoneOff} label="طلاب بدون جوال" value={data.students.without_phone} color="bg-rose-100 text-rose-600" />
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">أداء الرسائل الشهري (آخر 6 أشهر)</CardTitle></CardHeader>
        <CardContent>
          <MonthlyChart monthly={data.monthly || []} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">توزيع الرسائل حسب النوع شهرياً</CardTitle></CardHeader>
        <CardContent>
          <MonthlyTypeChart monthly={data.monthly || []} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">تفصيل الرسائل حسب النوع</CardTitle></CardHeader>
        <CardContent className="p-0">
          {data.by_type.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-slate-400">
              <Send className="h-10 w-10" />
              <p>لا توجد رسائل مرسلة بعد</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">نوع الرسالة</TableHead>
                  <TableHead className="text-center">الإجمالي</TableHead>
                  <TableHead className="text-center">تم الإرسال</TableHead>
                  <TableHead className="text-center">فشل</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.by_type.map((t) => (
                  <TableRow key={t.template_id} data-testid={`type-row-${t.template_id}`}>
                    <TableCell className="font-semibold text-slate-800">{t.label}</TableCell>
                    <TableCell className="text-center">{t.total}</TableCell>
                    <TableCell className="text-center text-green-700">{t.sent}</TableCell>
                    <TableCell className="text-center text-red-600">{t.failed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
